/**
 * PKCE, RFC 7636.
 *
 * The client is public: a single-page application cannot keep a secret, so the
 * authorization code flow protects the code exchange with a proof key instead. The
 * verifier is generated here, kept in this browser only, and presented at the token
 * endpoint; only its SHA-256 hash travels through the address bar. An intercepted code is
 * therefore not enough to obtain a token.
 *
 * The realm requires S256 server-side (`pkce.code.challenge.method`), so a client that
 * omitted the challenge would be refused rather than silently downgraded.
 */

/** Bytes of entropy behind a verifier and a state value. 32 is the RFC's recommendation. */
const ENTROPY_BYTES = 32;

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** A URL-safe random string from the platform's cryptographic generator. */
export function randomUrlSafe(byteLength: number = ENTROPY_BYTES): string {
  const bytes = new Uint8Array(byteLength);
  crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
}

/** The S256 code challenge for a verifier. */
export async function codeChallengeS256(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64UrlEncode(new Uint8Array(digest));
}
